const API_BASE = '/api'

export interface MeshyTask {
  result: string
  status: 'PENDING' | 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED'
  model_urls?: {
    glb?: string
    fbx?: string
    obj?: string
  }
  model_url?: string
  thumbnail_url?: string
  progress?: number
}

export async function createImageTo3DTask(imageUrl: string): Promise<{ result: string }> {
  const response = await fetch(`${API_BASE}/meshy/image-to-3d`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      image_url: imageUrl,
      enable_pbr: true,
    }),
  })
  
  if (!response.ok) {
    throw new Error('Failed to create task')
  }
  
  return response.json()
}

export async function createMultiImageTo3DTask(imageUrls: string[]): Promise<{ result: string }> {
  const response = await fetch(`${API_BASE}/meshy/multi-image-to-3d`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      image_urls: imageUrls,
      enable_pbr: true,
    }),
  })
  
  if (!response.ok) {
    throw new Error('Failed to create task')
  }
  
  return response.json()
}

export async function createTextTo3DTask(prompt: string): Promise<{ result: string }> {
  const response = await fetch(`${API_BASE}/meshy/text-to-3d`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt,
      mode: 'preview',
      art_style: 'realistic',
    }),
  })
  
  if (!response.ok) {
    throw new Error('Failed to create task')
  }
  
  return response.json()
}

export async function getTaskStatus(taskId: string, mode: string = 'image'): Promise<MeshyTask> {
  const endpoint = mode === 'multiimage' 
    ? `multi-image-to-3d/${taskId}`
    : mode === 'text'
    ? `text-to-3d/${taskId}`
    : `image-to-3d/${taskId}`
    
  const response = await fetch(`${API_BASE}/meshy/${endpoint}`)
  
  if (!response.ok) {
    throw new Error('Failed to get task status')
  }
  
  return response.json()
}

export async function saveModel(data: {
  task_id: string
  model_url: string
  thumbnail_url: string
  model_name?: string
  prompt?: string
}): Promise<{ success: boolean; model?: any }> {
  const token = localStorage.getItem('auth_token')
  
  if (!token) {
    throw new Error('Not authenticated')
  }
  
  const response = await fetch(`${API_BASE}/models/save`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  })
  
  if (!response.ok) {
    throw new Error('Failed to save model')
  }
  
  return response.json()
}

export async function getModelsList(): Promise<{ success: boolean; models: any[] }> {
  const token = localStorage.getItem('auth_token')
  
  if (!token) {
    throw new Error('Not authenticated')
  }
  
  const response = await fetch(`${API_BASE}/models/list?limit=100`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to get models list')
  }
  
  return response.json()
}

// ==================== Auth API ====================

export interface LoginResponse {
  success: boolean
  token: string
  user: {
    id: number
    email: string
    username: string
  }
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  
  const data = await response.json()
  
  if (!response.ok) {
    throw new Error(data.error || '登录失败')
  }
  
  return data
}

export async function register(email: string, password: string, username?: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, username }),
  })
  
  const data = await response.json()
  
  if (!response.ok) {
    throw new Error(data.error || '注册失败')
  }
  
  return data
}

export async function getCurrentUser(): Promise<{ success: boolean; user: any }> {
  const token = localStorage.getItem('auth_token')
  
  if (!token) {
    throw new Error('Not authenticated')
  }
  
  const response = await fetch(`${API_BASE}/auth/me`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to get user info')
  }
  
  return response.json()
}

// Cache model to local storage (no auth required)
export async function cacheModel(data: {
  task_id: string
  model_url: string
  thumbnail_url?: string
}): Promise<{ success: boolean; task_id: string; model_url: string; thumbnail_url?: string }> {
  const response = await fetch(`${API_BASE}/models/cache`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  
  if (!response.ok) {
    throw new Error('Failed to cache model')
  }
  
  return response.json()
}
